import { jsPDF } from 'jspdf';
import { exportJSON, safeName, setPdfHook, download } from './exporters';
import { exportPDF } from './pdf';
import { secOf, DETECT_SUBS } from './profiles';
import { tl } from './terms';

// desktop project archive in a real folder (File System Access API): GISS-Archiv/Branche/Bereich/Projekt/Berichte
const ROOT = 'GISS-Archiv', INDEX = 'archiv-index.json', PFILE = 'projekt.giss.json', REP = 'Berichte', BAK = 'Sicherungen';
export const BRANCH_DIR = { detect: 'Leakbuster', aqua: 'DripX', storm: 'Stormy', pipe: 'Mr. Pipe', building: 'Archi', garden: 'Terra', photo: 'Pixi', survey: 'GeoX', outdoor: 'Luna' };
export const archiveSupported = () => typeof window !== 'undefined' && 'showDirectoryPicker' in window;

const idb = () => new Promise((res, rej) => { const r = indexedDB.open('GissArchive', 1); r.onupgradeneeded = () => r.result.createObjectStore('h'); r.onsuccess = () => res(r.result); r.onerror = rej; });
const hGet = async () => { const d = await idb(); return new Promise((res) => { const q = d.transaction('h').objectStore('h').get('root'); q.onsuccess = () => res(q.result || null); q.onerror = () => res(null); }); };
const hSet = async (v) => { const d = await idb(); return new Promise((res) => { const tx = d.transaction('h', 'readwrite'); tx.objectStore('h').put(v, 'root'); tx.oncomplete = res; }); };

let root = null;
const emit = () => window.dispatchEvent(new Event('giss-archive'));
export const archiveReady = () => !!root;
export const archiveName = () => root?.name || '';

// silent reconnect on start (works when the browser kept the permission)
export async function archiveRestore() {
  if (!archiveSupported()) return false;
  const h = await hGet().catch(() => null);
  if (h && (await h.queryPermission({ mode: 'readwrite' })) === 'granted') { root = await h.getDirectoryHandle(ROOT, { create: true }); emit(); }
  return !!root;
}
// needs a click: re-grant the stored folder or pick a new one
export async function archiveConnect(pickNew = false) {
  let h = pickNew ? null : await hGet().catch(() => null);
  if (h && (await h.requestPermission({ mode: 'readwrite' })) !== 'granted') h = null;
  if (!h) { h = await window.showDirectoryPicker({ id: 'giss-archive', mode: 'readwrite', startIn: 'documents' }); await hSet(h); }
  root = await h.getDirectoryHandle(ROOT, { create: true });
  emit();
  return root.name;
}
export const archiveHasStored = async () => !!(await hGet().catch(() => null));

const dir = async (base, parts) => { let d = base; for (const p of parts) d = await d.getDirectoryHandle(p, { create: true }); return d; };
const write = async (d, name, data) => { const w = await (await d.getFileHandle(name, { create: true })).createWritable(); await w.write(data); await w.close(); };
const readJSON = async (d, name) => { try { return JSON.parse(await (await (await d.getFileHandle(name)).getFile()).text()); } catch { return null; } };
export const readIndex = async () => (await readJSON(root, INDEX)) || { projects: {} };
const saveIndex = (ix) => write(root, INDEX, JSON.stringify(ix, null, 1));

const SUB_NAME = { water: 'Wasser', gas: 'Gas', cable: 'Kabelfehler', sewer: 'Kanal', inhouse: 'Inhouse', locate: 'Leitungsortung', heat: 'Fernwaerme', roof: 'Dach' };
const areaOf = (p, t) => {
  if (p.sector === 'detect') return SUB_NAME[DETECT_SUBS[p.sub] ? p.sub : ''] || t.arAreaGeneral;
  return p.mode === 'single' ? t.arAreaSingle : t.arAreaProjects;
};
export const pathOf = (p, t) => [BRANCH_DIR[secOf(p)] || t.arBranchOther, areaOf(p, t), `${safeName(p.title)}_${p.id.slice(-5)}`];
const repFile = (r) => `${safeName(r.title)}_${r.id.slice(-4)}.pdf`;
const stable = (name) => name.replace(/_\d{4}-\d{2}-\d{2}(?=\.pdf$)/i, '');
const uniq = (l) => [...new Set(l.filter(Boolean).map((s) => String(s).trim()))].join(' | ');
// searchable place / customer / dates kept in the index
const meta = (p) => ({
  place: uniq([p.address, ...(p.reports || []).map((r) => r.address)]).slice(0, 400),
  customer: uniq([p.description, ...(p.reports || []).map((r) => r.job?.customer)]).slice(0, 600),
  createdAt: p.createdAt,
});

// write project file + one PDF per changed report; same report = overwrite, new report = added
// one archive write at a time, so the shared index never loses entries
let queue = Promise.resolve();
const serial = (fn) => (...a) => { const r = queue.then(() => fn(...a)); queue = r.catch(() => {}); return r; };
export const archiveProject = serial(async (project, lang, onStep, src) => {
  const t = tl(lang), ix = await readIndex(), old = ix.projects[project.id];
  const path = old?.path || pathOf(project, t), pd = await dir(root, path), rd = await dir(pd, [REP]);
  await write(pd, PFILE, await exportJSON(project, true));
  const reps = { ...(old?.reports || {}) }, list = project.reports || [];
  for (let i = 0; i < list.length; i++) {
    const r = list[i], prev = reps[r.id];
    onStep?.(i + 1, list.length);
    if (prev && prev.updatedAt === (r.updatedAt || prev.updatedAt) && prev.title === r.title) continue;
    const file = repFile(r), blob = await exportPDF(project, [r], lang, { blob: true });
    await write(rd, file, blob);
    if (prev?.file && prev.file !== file) await rd.removeEntry(prev.file).catch(() => {});
    reps[r.id] = { file, title: r.title, updatedAt: r.updatedAt || Date.now() };
  }
  ix.projects[project.id] = { ...old, path, title: project.title, sector: secOf(project) || '', mode: project.mode, reports: reps, archivedAt: Date.now(), updatedAt: project.updatedAt, ...meta(project), ...(src === 'phone' ? { phoneAt: Date.now() } : {}) };
  await saveIndex(ix);
  emit();
  return path;
});
// separate dated copy only on request
export const archiveBackup = serial(async (project) => {
  const ix = await readIndex(), e = ix.projects[project.id];
  if (!e) return false;
  const d = new Date(), tag = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}`;
  await write(await dir(root, [...e.path, BAK]), `${tag}.giss.json`, await exportJSON(project, true));
  return true;
});
export async function archiveFile(id, rel) {
  const e = (await readIndex()).projects[id];
  if (!e) return null;
  const d = await dir(root, rel.length > 1 ? [...e.path, ...rel.slice(0, -1)] : e.path);
  return (await d.getFileHandle(rel[rel.length - 1])).getFile();
}
export const archiveProjectFile = (id) => archiveFile(id, [PFILE]);
export const archiveReportFile = (id, file) => archiveFile(id, [REP, file]);

// PDFs created on this PC also land in the open project's report folder
let current = null, curLang = 'de';
export const setArchiveProject = (p, lang) => { current = p; curLang = lang || curLang; };
jsPDF.API.save = function save(name, o) { download(this.output('blob'), name || 'export.pdf'); return o?.returnPromise ? Promise.resolve() : this; };
setPdfHook(serial(async (blob, name) => {
  if (!root || !current) return;
  try {
    const ix = await readIndex(), t = tl(curLang);
    const e = ix.projects[current.id] || { path: pathOf(current, t), title: current.title, sector: secOf(current) || '', mode: current.mode, reports: {}, ...meta(current) };
    const pd = await dir(root, e.path), file = stable(name);
    if (!ix.projects[current.id]) await write(pd, PFILE, await exportJSON(current, true));
    await write(await dir(pd, [REP]), file, blob);
    e.extra = [...new Set([...(e.extra || []), file])];
    ix.projects[current.id] = { ...e, archivedAt: Date.now() };
    await saveIndex(ix);
    emit();
  } catch { /* archive folder not writable -> normal download only */ }
}));
